'use client';

import type { ComponentProps, ReactNode } from 'react';
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { cn } from '@savvy-kit/lib/utils';
import { CheckIcon, MinusIcon } from 'lucide-react';

import { Label } from '../label/label';

type CheckedState = boolean | 'indeterminate';

type CheckboxProps = Omit<
  ComponentProps<typeof CheckboxPrimitive.Root>,
  'checked' | 'defaultChecked' | 'indeterminate' | 'onCheckedChange'
> & {
  readonly checked?: CheckedState;
  readonly defaultChecked?: CheckedState;
  readonly onCheckedChange?: (checked: CheckedState) => void;
  // A string is wrapped in the kit Label so it picks up the label typography and
  // htmlFor wiring. A node is rendered inside that Label as-is, for callers whose
  // label is itself a component (e.g. a Badge or a link-bearing sentence).
  readonly label?: ReactNode;
  readonly description?: string;
  readonly id?: string;
};

function Checkbox({
  className,
  label,
  description,
  id,
  checked,
  defaultChecked,
  onCheckedChange,
  ...props
}: CheckboxProps) {
  const isIndeterminate = checked === 'indeterminate' || defaultChecked === 'indeterminate';

  return (
    <div className={cn('flex gap-2', className)}>
      <CheckboxPrimitive.Root
        data-slot="checkbox"
        checked={checked === 'indeterminate' ? false : checked}
        defaultChecked={defaultChecked === 'indeterminate' ? false : defaultChecked}
        indeterminate={isIndeterminate}
        onCheckedChange={onCheckedChange}
        className={cn(
          'peer focus-visible:ring-border-light aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border bg-card shadow-xs transition-shadow outline-none focus-visible:ring-2 data-disabled:cursor-not-allowed hover:cursor-pointer',
          'data-checked:bg-primary data-checked:text-primary-foreground dark:data-checked:bg-primary data-checked:border-primary',
          'data-indeterminate:bg-primary data-indeterminate:text-primary-foreground dark:data-indeterminate:bg-primary data-indeterminate:border-primary',
          'data-disabled:opacity-50 data-disabled:data-unchecked:bg-secondary',
        )}
        id={id}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          data-slot="checkbox-indicator"
          className="flex items-center justify-center text-current transition-none"
        >
          {isIndeterminate ? (
            <MinusIcon className="size-3.5 text-white" />
          ) : (
            <CheckIcon className="size-3.5 text-white" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <div className="pt-[1px] flex flex-col gap-0.5">
        {label !== undefined &&
          label !== null &&
          label !== '' &&
          (typeof label === 'string' ? (
            <Label htmlFor={id}>{label}</Label>
          ) : (
            // Still a <label> so clicking the node toggles the box, but Label's own
            // text styling is dropped — the node owns its appearance.
            <Label htmlFor={id} className="text-inherit font-normal">
              {label}
            </Label>
          ))}
        {description && <p className="text-secondary text-sm">{description}</p>}
      </div>
    </div>
  );
}

export { Checkbox };
